import { FaCircleCheck, FaTriangleExclamation, FaCirclePlus } from 'react-icons/fa6'
import * as Sentry from '@sentry/react'
import { LuCalendarClock } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import type { Contact, Status } from '@services/contribution/contribution'

export interface InvitationListItemProps {
  contact: Contact
  isParent: boolean
  onClick?: (contact: Contact) => void
}

function StatusIcon({ status }: { status: Status }) {
  switch (status) {
    case 'pending':
      return (
        <div className='flex items-center justify-center'>
          <LuCalendarClock
            className='w-5 h-5 rounded-md text-tangerine-orange'
            aria-label='Scheduled'
            title='Scheduled'
          />
        </div>
      )
    case 'success':
      return (
        <div className='flex items-center justify-center'>
          <FaCircleCheck className='w-5 h-5 text-leaf-green' aria-label='Sent' title='Sent' />
        </div>
      )
    case 'failure':
    case 'failure-editable-phone':
    case 'failure-editable-email':
      return (
        <div className='flex items-center justify-center'>
          <FaTriangleExclamation className='w-5 h-5 text-error' aria-label='Failed' title='Failed' />
        </div>
      )
    case 'na':
      return (
        <div className='flex items-center justify-center'>
          <FaCirclePlus
            className='w-5 h-5 text-cta'
            aria-label='Add Contact Information'
            title='Add Contact Information'
          />
        </div>
      )
  }
}

export function InvitationListHeader() {
  const { t } = useTranslation()
  const columnStyling = `py-2 px-1 text-sm font-semibold text-gray-700 sm:pl-0`
  return (
    <tr>
      <th scope='col' className={`${columnStyling} text-left`}>
        {t('content.components.current_contacts.name_column_title')}
      </th>
      <th scope='col' className={`${columnStyling} text-center`}>
        {t('content.components.current_contacts.sms_status_column_title')}
      </th>
      <th scope='col' className={`${columnStyling} text-center`}>
        {t('content.components.current_contacts.email_status_column_title')}
      </th>
    </tr>
  )
}

export function InvitationListItem({
  contact,
  isParent,
  onClick = (contact: Contact) => Sentry.logger.debug(Sentry.logger.fmt`Edit Contact: ${contact.name}`),
}: InvitationListItemProps) {
  const rowStyling =
    'whitespace-nowrap py-3 px-1 text-sm text-gray-500 group-hover:text-cta group-hover:bg-gray-100 group-hover:cursor-pointer'
  return (
    <tr className='group' key={contact.id} onClick={() => onClick(contact)}>
      <td className={`${rowStyling} text-left`}>
        {contact.name}
        {isParent && <span className='text-cta'> (Yourself)</span>}
      </td>
      <td className={`${rowStyling} text-center`}>
        <StatusIcon status={contact.smsStatus} />
      </td>
      <td className={`${rowStyling} text-center`}>
        <StatusIcon status={contact.emailStatus} />
      </td>
    </tr>
  )
}
